import { certificationRoutes } from '@/features/certification-guide/routes';
import type { RouteObject } from 'react-router';

import type { RouteHandle } from '@/hooks/useRouteHandle';

import { MobileEntryRedirect } from './components/MobileEntryRedirect';

const page = (name: string) => () =>
  import(`./pages/${name}.tsx`).then((m) => ({ Component: m[name] }));

/**
 * 모바일 화면(specs/037, contracts/mobile-routes.md) — `/workspace/m` 하위, 전부 lazy(PC 사용자는 내려받지 않는다).
 * 목록에 없는 주소는 PC 이용 안내로 받는다(FR-005).
 */
export const mobileRoutes: RouteObject[] = [
  // 첫 화면 = 허용된 첫 탭(지도사 공고 검색 #204 · 기업 홈 specs/065) — 기존 주소(`/workspace/m`)는 기록 교체로 넘긴다.
  { index: true, element: <MobileEntryRedirect /> },
  {
    path: 'home',
    lazy: page('MobileHomePage'),
    handle: { title: '홈', feature: 'COMPANY_HOME' } satisfies RouteHandle,
  },
  {
    // 기업 공고 찾기(specs/069) — 기업 셀프 묶음 + 판정 기능을 함께 요구(축 분기 없음)
    path: 'my-announcements',
    handle: { feature: 'COMPANY_HOME' } satisfies RouteHandle,
    children: [
      {
        index: true,
        lazy: page('MobileMyAnnouncementsPage'),
        handle: { title: '공고 찾기', feature: 'COMPANY_JUDGEMENT' } satisfies RouteHandle,
      },
    ],
  },
  {
    // 기업 성장 로드맵(specs/071) — 화면이 ROADMAP 기능도 확인한다
    path: 'my-roadmap',
    lazy: page('MobileMyRoadmapPage'),
    handle: { title: '성장 로드맵', feature: 'COMPANY_HOME' } satisfies RouteHandle,
  },
  {
    path: 'my-company',
    lazy: page('MobileMyCompanyPage'),
    handle: { title: '우리 회사', feature: 'COMPANY_HOME' } satisfies RouteHandle,
  },
  {
    path: 'my-company/onboarding',
    lazy: page('MobileOnboardingPage'),
    handle: { title: '회사 정보 넣기', feature: 'COMPANY_HOME' } satisfies RouteHandle,
  },
  {
    path: 'companies',
    lazy: page('MobileCompanyListPage'),
    handle: { title: '고객사', feature: 'MULTI_COMPANY' } satisfies RouteHandle,
  },
  {
    path: 'companies/new',
    lazy: page('MobileCompanyCreatePage'),
    // 고객사 등록·정보는 지도사 화면 — 기업 회원은 우리 회사 탭(specs/065 SC-004).
    handle: { title: '고객사 등록', feature: 'MULTI_COMPANY' } satisfies RouteHandle,
  },
  {
    path: 'companies/:id',
    lazy: page('MobileCompanyInfoPage'),
    handle: { title: '고객사 정보', feature: 'MULTI_COMPANY' } satisfies RouteHandle,
  },
  {
    path: 'companies/:id/match',
    lazy: page('MobileCompanyMatchPage'),
    handle: { title: '맞는 공고', feature: 'ANNOUNCEMENT_FINDER' } satisfies RouteHandle,
  },
  {
    path: 'search',
    lazy: page('MobileSearchPage'),
    handle: { title: '공고 검색', feature: 'ANNOUNCEMENT_FINDER' } satisfies RouteHandle,
  },
  {
    path: 'announcements/:id',
    lazy: page('MobileAnnouncementDetailPage'),
    handle: { title: '공고 상세', feature: 'ANNOUNCEMENT_FINDER' } satisfies RouteHandle,
  },
  {
    path: 'consultant-announcements',
    lazy: page('MobileConsultantAnnouncementListPage'),
    handle: { title: '컨설턴트 공고', feature: 'CONSULTANT_ANNOUNCEMENT' } satisfies RouteHandle,
  },
  {
    path: 'consultant-announcements/:id',
    lazy: page('MobileConsultantAnnouncementDetailPage'),
    handle: {
      title: '컨설턴트 공고 상세',
      feature: 'CONSULTANT_ANNOUNCEMENT',
    } satisfies RouteHandle,
  },
  {
    // 모바일 사업 상세(specs/092) — 공고 찾기 사업 카드·관심 사업에서 PC 이용 안내 없이 바로 온다(PC 와 같은 기능 키)
    path: 'programs/:id',
    lazy: page('MobileProgramDetailPage'),
    handle: { title: '사업 상세', feature: 'PROGRAM_DETAIL' } satisfies RouteHandle,
  },
  ...certificationRoutes(),
  { path: '*', lazy: page('MobilePcOnlyPage'), handle: { title: 'PC 전용 기능' } },
];
